import { createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { apiJson } from '@/lib/api';
import type {
  AdminUserDetailResponse,
  ExpenseInsightsPeriod,
  ExpenseInsightsResponse,
} from '@/lib/types';
import {
  BalanceOverTimeChart,
  BeneficiaryBreakdownChart,
  CategoryBreakdownChart,
  ContributionFairnessChart,
  InsightsPanel,
  MonthlySpendChart,
  OutstandingReimbursementsPanel,
  SettlementProgressPanel,
} from '@/components/expense-insights/ExpenseInsightCharts';

const PERIOD_OPTIONS: { value: ExpenseInsightsPeriod; label: string }[] = [
  { value: 'this_month', label: 'This month' },
  { value: 'last_3_months', label: 'Last 3 months' },
  { value: 'ytd', label: 'YTD' },
  { value: 'all', label: 'All time' },
];

function AdminUserGraphsPage() {
  const { userId } = Route.useParams();
  const id = Number(userId);
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const searchStr = useRouterState({ select: (s) => s.location.search });
  const hid = useMemo(() => new URLSearchParams(searchStr).get('hid') ?? '', [searchStr]);
  const hidNum = Number(hid);

  const period: ExpenseInsightsPeriod =
    search.period && PERIOD_OPTIONS.some((p) => p.value === search.period)
      ? search.period
      : 'this_month';

  const [metric, setMetric] = useState<'household' | 'my_share'>('household');

  const userQuery = useQuery({
    queryKey: ['admin', 'user-detail', id],
    queryFn: () => apiJson<AdminUserDetailResponse>(getToken, `/v1/admin/users/${id}`),
  });

  const canView =
    !!hid &&
    Number.isFinite(hidNum) &&
    (userQuery.data?.households.some((h) => h.household_id === hidNum) ?? false);

  const insightsQuery = useQuery({
    queryKey: ['admin', 'household-expense-insights', hidNum, id, period],
    queryFn: () =>
      apiJson<ExpenseInsightsResponse>(
        getToken,
        `/v1/admin/households/${hidNum}/expense-insights?perspective_user_id=${id}&period=${period}`,
      ),
    enabled: canView,
  });

  const household = userQuery.data?.households.find((h) => h.household_id === hidNum);
  const otherMember = household?.members.find((m) => m.user_id !== id);
  const myLabel =
    [userQuery.data?.first_name, userQuery.data?.last_name].filter(Boolean).join(' ') ||
    'This user';
  const otherLabel = otherMember
    ? [otherMember.first_name, otherMember.last_name].filter(Boolean).join(' ') ||
      `User #${otherMember.user_id}`
    : 'Coparent';

  const setPeriod = (next: ExpenseInsightsPeriod) => {
    void navigate({
      to: '/admin/users/$userId/graphs',
      params: { userId },
      search: { hid: hid || undefined, period: next },
      replace: true,
    });
  };

  if (!hid || !canView) {
    return (
      <p className='text-sm text-nova-muted-foreground'>
        Select a valid household (use the household chips above).
      </p>
    );
  }

  const data = insightsQuery.data;

  return (
    <div className='space-y-4'>
      <div className='flex flex-wrap items-start justify-between gap-3'>
        <div>
          <h2 className='text-lg font-semibold text-nova-foreground'>
            Graphs · household #{hidNum}
          </h2>
          <p className='text-sm text-nova-muted-foreground mt-0.5'>
            Expense insights from this user&apos;s perspective. Outstanding is all-time;
            other charts respect the selected period.
          </p>
        </div>
        <div className='flex flex-wrap items-center gap-2'>
          <div className='inline-flex rounded-md border border-nova-border bg-nova-card p-0.5'>
            {PERIOD_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type='button'
                onClick={() => setPeriod(opt.value)}
                className={`px-2.5 py-1.5 text-xs font-medium rounded ${
                  period === opt.value
                    ? 'bg-nova-primary text-white'
                    : 'text-nova-muted-foreground hover:bg-nova-accent'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className='inline-flex rounded-md border border-nova-border bg-nova-card p-0.5'>
            {(
              [
                { value: 'household' as const, label: 'Household $' },
                { value: 'my_share' as const, label: 'Their share' },
              ] as const
            ).map((opt) => (
              <button
                key={opt.value}
                type='button'
                onClick={() => setMetric(opt.value)}
                className={`px-2.5 py-1.5 text-xs font-medium rounded ${
                  metric === opt.value
                    ? 'bg-nova-primary text-nova-primary-foreground'
                    : 'text-nova-muted-foreground hover:bg-nova-accent'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {insightsQuery.isLoading ? (
        <div className='flex justify-center py-12'>
          <div className='animate-spin h-8 w-8 border-2 border-nova-primary border-t-transparent rounded-full' />
        </div>
      ) : insightsQuery.isError ? (
        <p className='text-sm text-red-600'>Could not load expense insights.</p>
      ) : !data ? null : (
        <div className='grid grid-cols-1 xl:grid-cols-2 gap-4'>
          <InsightsPanel
            title='1. Category spend'
            subtitle={
              metric === 'my_share'
                ? 'This user’s split share by category'
                : 'Household totals by category'
            }
          >
            <CategoryBreakdownChart items={data.category_breakdown} metric={metric} />
          </InsightsPanel>

          <InsightsPanel
            title='2. Who paid vs who owes'
            subtitle='Purchases, split shares, and payments in this period'
          >
            <ContributionFairnessChart
              fairness={data.contribution_fairness}
              myLabel={myLabel}
              otherLabel={otherLabel}
            />
          </InsightsPanel>

          <InsightsPanel
            title='3. Outstanding reimbursements'
            subtitle='All unpaid / partially paid expenses (not filtered by period)'
          >
            <OutstandingReimbursementsPanel outstanding={data.outstanding} />
          </InsightsPanel>

          <InsightsPanel
            title='4. Balance over time'
            subtitle='Month-end net owed (positive = this user owes)'
          >
            <BalanceOverTimeChart points={data.balance_over_time} />
          </InsightsPanel>

          <InsightsPanel
            title='5. Spend by child / beneficiary'
            subtitle='Multi-kid expenses are split evenly across tagged beneficiaries'
          >
            <BeneficiaryBreakdownChart items={data.beneficiary_breakdown} metric={metric} />
          </InsightsPanel>

          <InsightsPanel
            title='6. Monthly spend trend'
            subtitle='Total expense volume, stacked by recurring vs one-off'
          >
            <MonthlySpendChart points={data.monthly_spend} />
          </InsightsPanel>

          <div className='xl:col-span-2'>
            <InsightsPanel
              title='7. Settlement progress'
              subtitle='How much of reimbursable spend created in this period has been paid back'
            >
              <SettlementProgressPanel progress={data.settlement_progress} />
            </InsightsPanel>
          </div>
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute('/admin/users/$userId/graphs')({
  validateSearch: (search: Record<string, unknown>) => ({
    hid: typeof search.hid === 'string' ? search.hid : undefined,
    period:
      typeof search.period === 'string' &&
      ['this_month', 'last_3_months', 'ytd', 'all'].includes(search.period)
        ? (search.period as ExpenseInsightsPeriod)
        : undefined,
  }),
  component: AdminUserGraphsPage,
});
